import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import fantasyLogo from '../assets/jerseys/Logo 36.png';

const NAV_ITEMS = [
  {
    path: '/MiEquipo',
    label: 'Mi Equipo',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    path: '/Ligas',
    label: 'Ligas',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
  },
  {
    path: '/Fechas',
    label: 'Fechas',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    path: '/Reglamento',
    label: 'Reglas',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
  },
];

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  const isActive = (path: string) => {
    if (path === '/MiEquipo') {
      return location.pathname === '/MiEquipo' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setShowProfileDropdown(false);
  };

  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('fantasyTeamId');
    navigate('/auth', { replace: true });
    window.location.reload();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.profile-dropdown-container')) {
        setShowProfileDropdown(false);
      }
    };
    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileDropdown]);

  return (
    <>
      {/* ── TOP NAV (desktop) ── */}
      <nav className="hidden md:flex bg-lub-dark text-white shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 w-full flex items-center justify-between h-16">

          {/* Logo */}
          <NavLink to="/MiEquipo" className="flex items-center gap-3 flex-shrink-0">
            <img src={fantasyLogo} alt="SuperDt LUB" className="h-10 w-10 object-contain" />
            <span className="font-bold text-lg tracking-tight">
              Fantasy<span className="text-lub-accent">Lub</span>
            </span>
          </NavLink>

          {/* Links */}
          <div className="flex items-center gap-1">
            {NAV_ITEMS.map(({ path, label }) => (
              <NavLink
                key={path}
                to={path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(path)
                    ? 'bg-lub-blue text-white'
                    : 'text-blue-200 hover:bg-lub-blue/30 hover:text-white'
                }`}
              >
                {label}
              </NavLink>
            ))}
          </div>

          {/* Perfil */}
          <div className="relative profile-dropdown-container">
            <button
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              className="flex items-center justify-center w-9 h-9 bg-lub-blue hover:bg-lub-light text-white rounded-full transition-all duration-200"
              title="Mi perfil"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </button>

            {showProfileDropdown && (
              <div className="absolute right-0 top-[calc(100%+0.5rem)] w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50 animate-fade-in">
                <button
                  onClick={() => { setShowProfileDropdown(false); navigate('/Perfil'); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-lub-pale flex items-center gap-2"
                >
                  <svg className="w-4 h-4 text-lub-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Editar Perfil
                </button>
                <button
                  onClick={handleLogoutClick}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Cerrar Sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ── MOBILE HEADER (solo logo + perfil arriba) ── */}
      <header className="md:hidden bg-lub-dark text-white flex items-center justify-between px-4 h-14 sticky top-0 z-40 shadow">
        <NavLink to="/MiEquipo" className="flex items-center gap-2">
          <img src={fantasyLogo} alt="SuperDt LUB" className="h-8 w-8 object-contain" />
          <span className="font-bold text-base tracking-tight">
            Fantasy<span className="text-lub-accent">Lub</span>
          </span>
        </NavLink>

        <div className="relative profile-dropdown-container">
          <button
            onClick={() => setShowProfileDropdown(!showProfileDropdown)}
            className="flex items-center justify-center w-9 h-9 bg-lub-blue text-white rounded-full"
            title="Mi perfil"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </button>

          {showProfileDropdown && (
            <div className="absolute right-0 top-[calc(100%+0.5rem)] w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50 animate-fade-in">
              <button
                onClick={() => { setShowProfileDropdown(false); navigate('/Perfil'); }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-lub-pale flex items-center gap-2"
              >
                <svg className="w-4 h-4 text-lub-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Editar Perfil
              </button>
              <button
                onClick={handleLogoutClick}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 flex items-center gap-2"
              >
                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 013 3v1" />
                </svg>
                Cerrar Sesión
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ── BOTTOM NAV (mobile) ── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-lub-dark border-t border-lub-blue/30 pb-safe">
        <div className="flex items-center justify-around h-16">
          {NAV_ITEMS.map(({ path, label, icon }) => {
            const active = isActive(path);
            return (
              <NavLink
                key={path}
                to={path}
                className="flex flex-col items-center justify-center flex-1 h-full gap-1 transition-all duration-200"
              >
                <span className={`transition-colors duration-200 ${active ? 'text-lub-accent' : 'text-blue-300'}`}>
                  {icon}
                </span>
                <span className={`text-[10px] font-medium transition-colors duration-200 ${
                  active ? 'text-lub-accent' : 'text-blue-300'
                }`}>
                  {label}
                </span>
                {active && (
                  <span className="absolute top-0 w-8 h-0.5 bg-lub-accent rounded-full" />
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Modal logout */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-fade-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-gray-900">Cerrar Sesión</h2>
            </div>
            <p className="text-gray-600 text-sm mb-6">
              ¿Seguro que querés cerrar sesión?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmLogout}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
